<template>
  <div id="kv-field">
    <p class="cf-text-dimmed">
      <span class="debug-reload" v-if="debug" @click="reload">dev reload</span><br />
      <span v-if="!has_params">
        <em>No parameters are available to configure.</em>
      </span>
    </p>

    <div v-if="edit_mode">
      <textarea v-model="text_data" rows="15"></textarea>
      <button @click="saveHandEditedData">Save Changes</button>
      <button @click="setEditMode(false)">Cancel</button>
      <span class="edit-error" v-if="edit_error" v-html="edit_error"></span>
      <br /><br />
      <pre>
example configuration:

{
  // set multiple key:{options} for each field you need

  "art_prompts": { // custom unique identifier
    "min": 0, // min entries for this key
    "max": 5, // max entries for this key
    "data": [], // set to empty array (or string when max=1) on first setup
    "name": "Artwork Prompts", // presented to user
    "char_limit": [1, 30],
    "type": "string" // only string, for now
  }
}
      </pre>
    </div>

    <div v-else>
      <div v-for="field in fields" :key="field.id" class="kv-param">
        <strong>{{ field.name }}&nbsp;&nbsp;</strong>
        <span v-if="field.max > 1">({{field.data.length}}/{{field.max}})</span>&nbsp;&nbsp;
        <span v-if="field.char_limit">(max {{field.char_limit[1]}} chars)</span>
        <br />

        <!-- key : value -->
        <div v-if="field.max === 1">
          <input  type="text" :value="params[field.id].data"
            @input="updateValue(field.id, $event.target.value, undefined)" />

          <div class="edit-error" v-if="constraint_error">{{ constraint_error }}</div>
        </div>

        <div v-else>
          <!-- key : [value] -->
          <span v-for="(val, vidx) in params[field.id].data" :key="vidx">
            <input type="text" @input="updateValue(field.id, $event.target.value, vidx)"
              class="multi-input" :value="val" />

            <!-- only show delete if item idx meets min requirement -->
            <div class="del" v-if="vidx >= field.min"
              @click="deleteValue(field.id, vidx)">delete</div>
          </span>

          <div class="edit-error" v-if="constraint_error">{{ constraint_error }}</div>

          <button class="cf-btn-secondary" @click="addNew(field.id)"
            :disabled="field.data.length >= field.max">Add New</button>
        </div>
      </div>
    </div>

    <div class="edit-px" @click="setEditMode(!edit_mode)"></div>
  </div>
</template>

<script>
import FieldMixin from '../Field.mixin.vue';

export default {
  mixins: [FieldMixin],
  data () {
    return {
      save_timeout: null,
      save_wait: 1500,

      // edit mode use
      edit_mode: false,
      text_data: '',
      edit_error: '',

      constraint_error: ''
    };
  },
  methods: {
    setEditMode (emode) {
      this.edit_mode = emode;

      if (this.edit_mode) {
        this.text_data = JSON.stringify(this.params, null, 2);
      }
      else {
        this.edit_error = '';
      }
    },
    async saveHandEditedData () {
      this.edit_error = '';

      try {
        const data = JSON.parse(this.text_data);

        // detect transitioning from array to string, and convert the data
        Object.keys(data).forEach(key => {
          const opts = data[key];

          // we need to be a string
          if (opts.max === 1) {
            if (typeof opts.data !== 'string') {
              opts.data = opts.data[0] || '';
            }
          }
          // we need to be an array
          else if (!Array.isArray(opts.data)) {
            opts.data = [opts.data];
          }
        });

        await this.saveDataToContentful(data);
        this.loadDataFromContentful();
        this.setEditMode(false);
      }
      catch (err) {
        this.edit_error = '&nbsp;&nbsp; Failed to save. Check that your JSON syntax is error-free.';

        console.error('error during saveHandEditedData()', err.message);
      }
    },
    updateValue (key, value, idx) {
      clearTimeout(this.save_timeout);

      const lmt = this.params[key].char_limit;

      if (idx !== undefined) {
        this.params[key].data[idx] = value;
      }
      else {
        this.params[key].data = value;
      }

      // enforce char constraints before saving
      if (lmt && ((value.length > lmt[1] || value.length < lmt[0]))) {
        this.constraint_error = `Character count must be between ${lmt[0]} and ${lmt[1]}`;
        return;
      }
      else {
        this.constraint_error = '';
      }

      // console.log(this.params);
      this.save_timeout = setTimeout(this.saveDataToContentful, this.save_wait);
    },
    deleteValue (key, pos) {
      this.$delete(this.params[key].data, pos);

      clearTimeout(this.save_timeout);
      this.save_timeout = setTimeout(this.saveDataToContentful, this.save_wait);
    },
    addNew (key) {
      this.params[key].data.push('');
    },
    loadDataFromContentful () {
      const params = this.$extension.field.getValue();

      // console.log('contentful value', params);

      if (params) {
        console.log('found contentful data');
        this.params = params;
      }
      else {
        console.warn('no configuration found for key value field');
      }
    }
  },
  computed: {
    fields () {
      return Object.keys(this.params).map(key => {
        return Object.assign({}, { id: key }, this.params[key]);
      });
    },
    has_params () {
      return Object.keys(this.params).length > 0;
    }
  },
  watch: {
  },
  created () {
    // console.log(this.$extension);
    this.loadDataFromContentful();
  },
  mounted () {
  },
  beforeDestroy () {
  },
  components: {
  }
};
</script>

<style lang="scss" scoped>
@import url(https://contentful.github.io/ui-extensions-sdk/cf-extension.css);

#kv-field {
  width: 100%;
}

.kv-param {
  font-size: 14px;
  line-height: 18px;
  padding: 20px 0;
  border-bottom: 1px dashed #ccc;

  strong {
    display: inline-block;
    margin-bottom: 5px;
  }
}
.kv-param:first-of-type {
  padding-top: 0;
}
.kv-param:last-of-type {
  padding-bottom: 0;
  border-bottom: none;
}

input {
  font-size: 0.875rem;
  line-height: 1.25rem;
  padding: 10px 0.75rem;
  height: 40px;
  border-radius: 6px;;
  border: 1px solid rgb(207, 217, 224);
  color: rgb(65, 77, 99);
  width: 90%;
  outline: none;
  box-shadow: rgb(225 228 232 / 20%) 0px 2px 0px inset;
  box-sizing: border-box;
  margin: 4px 0;
}
input:focus {
  border-color: rgb(0, 89, 200);
  // box-shadow: rgb(152 203 255) 0px 0px 0px 3px;
}

.debug-reload {
  cursor: pointer;
}

.del {
  color: #fb5454;
  display: inline-block;
  width: 10%;
  text-align: center;
  cursor: pointer;
}

button{
  margin-top: 7px;
  text-align: center;
}

.edit-px {
  width: 4px;
  height: 4px;
  position: fixed;
  top: 0;
  right: 0;
  cursor: pointer;
  background-color: #ffefff;;
}

textarea {
  width: 100%;
}

.edit-error{
  color: #fb5454;
}

pre {
  font-family: monospace;
  font-size: 12px;
}
</style>
